/**
 * Dadverse section behaviours — db-sections.js (deferred; requires db-theme.js)
 * Small DOM-first enhancements. No GSAP here; cinematic scenes live in the
 * motion modules (Milestone 5) and only enhance what already works.
 *
 * Components:
 *  - rail:          prev/next buttons for .db-rail on desktop
 *  - hero-video:    play/pause toggle + autoplay guard for hero video
 *  - portal-story:  lite mode — step activation + stage plate crossfade (IO)
 *  - gift-finder:   maps answers to a destination URL
 *  - deferred-video: click-to-play videos with poster
 */
(() => {
  const DB = window.DB;
  if (!DB || typeof DB.register !== 'function') return;

  /* ---------------------------------------------------------------------- */
  DB.register('rail', (root) => {
    const rail = root.querySelector('.db-rail');
    const prev = root.querySelector('[data-db-rail-prev]');
    const next = root.querySelector('[data-db-rail-next]');
    if (!rail || !prev || !next) return () => {};

    const step = () => {
      const first = rail.firstElementChild;
      const gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || '0') || 0;
      return first ? first.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
    };
    const update = () => {
      const max = rail.scrollWidth - rail.clientWidth - 1;
      const isGrid = getComputedStyle(rail).display === 'grid';
      prev.disabled = isGrid || rail.scrollLeft <= 1;
      next.disabled = isGrid || rail.scrollLeft >= max;
      root.classList.toggle('db-rail--no-overflow', isGrid || max <= 1);
    };
    const go = (dir) => rail.scrollBy({ left: dir * step(), behavior: DB.motion === 'reduced' ? 'auto' : 'smooth' });
    const onPrev = () => go(-1);
    const onNext = () => go(1);
    prev.addEventListener('click', onPrev);
    next.addEventListener('click', onNext);
    rail.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
    return () => {
      prev.removeEventListener('click', onPrev);
      next.removeEventListener('click', onNext);
      rail.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  });

  /* ---------------------------------------------------------------------- */
  DB.register('hero-video', (root) => {
    const video = root.querySelector('video');
    const media = root.querySelector('.db-hero__media');
    const toggle = root.querySelector('[data-db-video-toggle]');
    if (!video || !media) return () => {};
    const labels = { play: toggle?.dataset.labelPlay || 'Play', pause: toggle?.dataset.labelPause || 'Pause' };

    const setState = (playing) => {
      media.classList.toggle('db-hero__media--paused', !playing);
      if (toggle) {
        toggle.setAttribute('aria-label', playing ? labels.pause : labels.play);
        toggle.querySelector('[data-icon-play]')?.toggleAttribute('hidden', playing);
        toggle.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !playing);
      }
    };

    const canAutoplay = DB.motion !== 'reduced' && !DB.capabilities.saveData;
    if (canAutoplay) {
      video.muted = true;
      const p = video.play();
      if (p && p.catch) p.catch(() => setState(false));
      setState(true);
    } else {
      video.pause();
      setState(false);
    }

    // Pause when offscreen to save battery.
    let io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          if (!canAutoplay || media.classList.contains('db-hero__media--paused')) return;
          entries[0].isIntersecting ? video.play().catch(() => {}) : video.pause();
        },
        { threshold: 0.05 }
      );
      io.observe(root);
    }

    const onToggle = () => {
      if (video.paused) {
        video.play().then(() => setState(true)).catch(() => setState(false));
      } else {
        video.pause();
        setState(false);
      }
    };
    toggle?.addEventListener('click', onToggle);
    return () => {
      toggle?.removeEventListener('click', onToggle);
      io?.disconnect();
      video.pause();
    };
  });

  /* ---------------------------------------------------------------------- */
  DB.register('portal-story', (root) => {
    // Lite behaviour only. The cinematic module (db-three-portal.js) takes
    // over in full mode and sets root.dataset.dbPortalMode = 'webgl'.
    const steps = [...root.querySelectorAll('[data-db-step]')];
    const plates = [...root.querySelectorAll('[data-db-plate]')];
    const stage = root.querySelector('[data-db-stage]');
    if (!steps.length || !stage) return () => {};

    const activate = (index) => {
      if (root.dataset.dbActive === String(index)) return;
      root.dataset.dbActive = String(index);
      steps.forEach((s, i) => s.classList.toggle('is-active', i === index));
      plates.forEach((p, i) => p.classList.toggle('is-active', i === index));
      const world = steps[index]?.dataset.dbWorld;
      if (world) stage.style.setProperty('--db-world', `var(--db-world-${world}, var(--db-accent))`);
      root.dispatchEvent(new CustomEvent('db:portal-step', { detail: { index, world } }));
    };

    activate(0);
    if (!('IntersectionObserver' in window)) return () => {};

    const io = new IntersectionObserver(
      (entries) => {
        // Pick the step closest to the viewport centre among intersecting ones.
        const visible = entries.filter((e) => e.isIntersecting);
        if (!visible.length) return;
        const mid = window.innerHeight / 2;
        let best = null;
        let bestD = Infinity;
        visible.forEach((e) => {
          const r = e.boundingClientRect;
          const d = Math.abs(r.top + r.height / 2 - mid);
          if (d < bestD) {
            bestD = d;
            best = e.target;
          }
        });
        if (best) activate(steps.indexOf(best));
      },
      { rootMargin: '-35% 0px -35% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    steps.forEach((s) => io.observe(s));

    // Editor: reveal the selected block.
    const onBlockSelect = (event) => {
      const id = event.detail?.blockId;
      const idx = steps.findIndex((s) => s.dataset.blockId === id);
      if (idx > -1) {
        activate(idx);
        steps[idx].scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    };
    root.addEventListener('db:block-select', onBlockSelect);

    return () => {
      io.disconnect();
      root.removeEventListener('db:block-select', onBlockSelect);
    };
  });

  /* ---------------------------------------------------------------------- */
  DB.register('gift-finder', (root) => {
    const form = root.querySelector('form');
    const dataEl = root.querySelector('[data-db-gift-map]');
    const result = root.querySelector('[data-db-gift-result]');
    if (!form || !dataEl) return () => {};
    let map = [];
    try {
      map = JSON.parse(dataEl.textContent || '[]');
    } catch (e) {
      map = [];
    }

    const score = (entry, answers) => {
      let s = 0;
      for (const key of ['who', 'into', 'budget']) {
        const want = (entry[key] || 'any').toLowerCase();
        const have = (answers[key] || '').toLowerCase();
        if (want === 'any') s += 1;
        else if (want === have) s += 3;
        else return -1;
      }
      return s;
    };

    const onSubmit = (event) => {
      const answers = {
        who: form.elements.who?.value,
        into: form.elements.into?.value,
        budget: form.elements.budget?.value,
      };
      let best = null;
      let bestScore = -1;
      map.forEach((entry) => {
        const s = score(entry, answers);
        if (s > bestScore) {
          bestScore = s;
          best = entry;
        }
      });
      if (best && best.url) {
        event.preventDefault();
        if (result) {
          result.hidden = false;
          const link = result.querySelector('a');
          if (link) {
            link.href = best.url;
            link.textContent = best.label || link.textContent;
          }
          result.focus?.();
        }
        window.location.assign(best.url);
      }
      // No match: let the form submit to its fallback action.
    };
    form.addEventListener('submit', onSubmit);
    return () => form.removeEventListener('submit', onSubmit);
  });

  /* ---------------------------------------------------------------------- */
  DB.register('deferred-video', (root) => {
    const button = root.querySelector('.db-video__play');
    const video = root.querySelector('video');
    if (!button || !video) return () => {};
    const onClick = () => {
      video.controls = true;
      video.muted = false;
      video.play().catch(() => {});
      button.hidden = true;
    };
    button.addEventListener('click', onClick);
    return () => button.removeEventListener('click', onClick);
  });
})();
